/**
 * Template data normalization utilities
 * Ensures consistent template structure across the application
 */

/**
 * Default template data structure
 */
export const DEFAULT_TEMPLATE_DATA = {
  messages: [
    { role: 'system', content: '' },
    { role: 'user', content: '' }
  ],
  prompt: '',
  role: 'user',
  test_cases: [{ input: '', expected: '' }],
  expected_output: '',
  evaluation_criteria: [],
  metadata: {},
};

/**
 * Normalize template data to ensure all required fields exist
 * @param {Object} template - Raw template object from API
 * @returns {Object} Normalized template with all required fields
 */
export function normalizeTemplateData(template) {
  if (!template) {
    return { template_data: { ...DEFAULT_TEMPLATE_DATA } };
  }

  // Ensure template_data exists
  const normalizedTemplate = {
    ...template,
    template_data: template.template_data || {},
  };

  const templateData = normalizedTemplate.template_data;

  // Normalize messages
  if (!Array.isArray(templateData.messages) || templateData.messages.length === 0) {
    templateData.messages = [...DEFAULT_TEMPLATE_DATA.messages];
  }

  // Normalize prompt (derive from messages if not present)
  if (!templateData.prompt) {
    const userMessage = templateData.messages.find(m => m.role === 'user');
    templateData.prompt = userMessage?.content || DEFAULT_TEMPLATE_DATA.prompt;
  }

  // Normalize role
  if (!templateData.role) {
    templateData.role = DEFAULT_TEMPLATE_DATA.role;
  }

  // Normalize test_cases
  if (!Array.isArray(templateData.test_cases) || templateData.test_cases.length === 0) {
    templateData.test_cases = [...DEFAULT_TEMPLATE_DATA.test_cases];
  }

  // Normalize expected_output
  if (templateData.expected_output === undefined || templateData.expected_output === null) {
    templateData.expected_output = DEFAULT_TEMPLATE_DATA.expected_output;
  }

  // Normalize evaluation_criteria
  if (!Array.isArray(templateData.evaluation_criteria)) {
    templateData.evaluation_criteria = [...DEFAULT_TEMPLATE_DATA.evaluation_criteria];
  }

  // Normalize metadata
  if (!templateData.metadata || typeof templateData.metadata !== 'object') {
    templateData.metadata = { ...DEFAULT_TEMPLATE_DATA.metadata };
  }

  return normalizedTemplate;
}

/**
 * Validate template data structure
 * @param {Object} templateData - Template data to validate
 * @returns {Object} Validation result with isValid flag and errors array
 */
export function validateTemplateData(templateData) {
  const errors = [];

  if (!templateData) {
    errors.push('Template data is required');
    return { isValid: false, errors };
  }

  // Validate messages
  if (!Array.isArray(templateData.messages)) {
    errors.push('Messages must be an array');
  } else if (templateData.messages.length === 0) {
    errors.push('At least one message is required');
  } else {
    templateData.messages.forEach((msg, index) => {
      if (!msg.role || !['system', 'user', 'assistant'].includes(msg.role)) {
        errors.push(`Message ${index + 1}: Invalid role`);
      }
      if (typeof msg.content !== 'string') {
        errors.push(`Message ${index + 1}: Content must be a string`);
      }
    });
  }

  // Validate test_cases
  if (Array.isArray(templateData.test_cases)) {
    templateData.test_cases.forEach((testCase, index) => {
      if (!testCase.input && !testCase.expected) {
        errors.push(`Test case ${index + 1}: Must have input or expected output`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Create a new empty template with default values
 * @param {Object} overrides - Optional field overrides
 * @returns {Object} New template object
 */
export function createEmptyTemplate(overrides = {}) {
  return {
    name: '',
    description: '',
    category: 'custom',
    visibility: 'private',
    tags: [],
    template_data: { ...DEFAULT_TEMPLATE_DATA },
    ...overrides,
  };
}

/**
 * Deep clone template data to prevent mutation
 * @param {Object} template - Template to clone
 * @returns {Object} Cloned template
 */
export function cloneTemplate(template) {
  return JSON.parse(JSON.stringify(template));
}
